import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, X, AlertCircle, ArrowLeft } from 'lucide-react';
import { StaticPageFormData } from '../../types/staticPage.types';
import { toast } from 'react-toastify';
import { staticPagesService } from '../../services/api/staticPagesService';

const SLUG_OPTIONS = [
  { value: 'about_organization', label: 'About Organization' },
  { value: 'functionaries', label: 'Functionaries' },
  { value: 'citizen_charter', label: 'Citizen Charter' },
  { value: 'prees', label: 'Prees' },
  { value: 'assets', label: 'Assets' },
  { value: 'cadre_strength', label: 'Cadre Strength' },
];

const StaticPagesForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<StaticPageFormData>({
    slug: '',
    section: '',
    title: '',
    content: '',
    metadata: null,
    display_order: 0,
    is_active: true,
  });

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof StaticPageFormData, string>>>({});

  useEffect(() => {
    if (isEditMode && id) {
      fetchPage(parseInt(id));
    }
  }, [id, isEditMode]);

  const fetchPage = async (pageId: number) => {
    try {
      setFetchingData(true);
      const response = await staticPagesService.getById(pageId);
      if (response.success) {
        const data = response.data;
        setFormData({
          slug: data.slug || '',
          section: data.section || '',
          title: data.title || '',
          content: data.content || '',
          metadata: data.metadata || null,
          display_order: data.display_order || 0,
          is_active: data.is_active,
        });
      }
    } catch (error) {
      toast.error('Failed to fetch static page details');
      console.error(error);
      navigate('/static-pages');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    if (errors[name as keyof StaticPageFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof StaticPageFormData, string>> = {};

    if (!formData.slug) {
      newErrors.slug = 'Slug is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be 0 or greater';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      if (isEditMode && id) {
        await staticPagesService.update(parseInt(id), formData);
        toast.success('Static page updated successfully');
      } else {
        await staticPagesService.create(formData);
        toast.success('Static page created successfully');
      }

      navigate('/static-pages');
    } catch (error: any) {
      const message = error.response?.data?.message ||
        (isEditMode ? 'Failed to update static page' : 'Failed to create static page');
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading static page data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Static Page' : 'Create Static Page'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Update static page content and settings'
              : 'Add a new static page entry'}
          </p>
        </div>
        <Link
          to="/static-pages"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to List
        </Link>
      </div>

      {/* Form Card */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Slug and Section Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Slug */}
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                Slug <span className="text-red-500">*</span>
              </label>
              <select
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.slug
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              >
                <option value="">Select Slug</option>
                {SLUG_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.slug && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.slug}</span>
                </div>
              )}
            </div>

            {/* Section */}
            <div>
              <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-2">
                Section
              </label>
              <input
                type="text"
                id="section"
                name="section"
                value={formData.section}
                onChange={handleChange}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., main, sidebar"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                errors.title
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Enter page title"
            />
            {errors.title && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.title}</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={12}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-y ${
                errors.content
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Enter the page content here..."
            />
            {errors.content && (
              <div className="flex items-center mt-2 text-sm text-red-600">
                <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                <span>{errors.content}</span>
              </div>
            )}
            <p className="mt-2 text-sm text-gray-500">
              Enter the content for this page. This will be displayed on the public website.
            </p>
          </div>

          {/* Display Order and Active Status Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                Display Order
              </label>
              <input
                type="number"
                id="display_order"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.display_order
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {errors.display_order ? (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle size={14} className="mr-1 flex-shrink-0" />
                  <span>{errors.display_order}</span>
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  Lower numbers appear first in the list
                </p>
              )}
            </div>

            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
              </label>
              <span className="ml-2 text-sm text-gray-500">(Visible on public website)</span>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/static-pages')}
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={18} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  {isEditMode ? 'Update Static Page' : 'Create Static Page'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaticPagesForm;
